/**
 * Read-only directory listing for the bridge's working directory.
 */

import fs from 'node:fs';
import { ensureUnderCwd } from '../ensure-under-cwd.js';
import { getBridgeRoot } from '../cwd/index.js';
import { yieldToEventLoop } from '../../runtime/yield-to-event-loop.js';
import { LIST_DIR_YIELD_EVERY, type ListEntry } from './types.js';
import { mapDirEntry } from './map-dir-entry.js';
import { sortListEntries } from './sort-entries.js';

export type { ListEntry } from './types.js';

/**
 * Lists a directory asynchronously, yielding to the event loop while resolving entries
 * so the bridge stays responsive on large directories.
 */
export async function listDirAsync(
  relativePath: string,
  sessionParentPath: string = getBridgeRoot(),
): Promise<{ entries: ListEntry[] } | { error: string }> {
  await yieldToEventLoop();
  const resolved = ensureUnderCwd(relativePath || '.', sessionParentPath);
  if (!resolved) {
    return { error: 'Path is outside working directory' };
  }
  try {
    const names = await fs.promises.readdir(resolved, { withFileTypes: true });
    const entries: ListEntry[] = [];
    for (let i = 0; i < names.length; i++) {
      if (i > 0 && i % LIST_DIR_YIELD_EVERY === 0) {
        await yieldToEventLoop();
      }
      entries.push(await mapDirEntry(names[i]!, relativePath, resolved));
    }
    return { entries: sortListEntries(entries) };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { error: message };
  }
}
